/**
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 🚀 NeuroAgentes [SimpleX] www.simplex.tec.br
 * 
 * Página de registro com assistente LEX integrado
 * Interface conversacional inteligente para coleta de dados de registro
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Avatar,
  Box,
  Chip,
  Paper,
  IconButton,
  InputAdornment,
  Fade,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Home as HomeIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { lexService } from '../../services/lexService';
import { PasswordStrengthIndicator } from '../../components/common/PasswordStrengthIndicator';
import { PasswordValidationResult } from '../../services/passwordValidationService';
import { buildApiUrl } from '../../config/api.config';

// Importando constantes de cores do tema centralizado
import { 
  VERDE_MENTA, 
  FUNDO_ESCURO,
  FUNDO_PAPEL,
  TEXTO_SECUNDARIO,
  withAlpha
} from '../../themes/themeConstants';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'lex' | 'system';
  timestamp: Date;
  suggestions?: string[];
  avatar?: string;
  showEmailExistsActions?: boolean;
  emailForLogin?: string;
}

interface RegistrationData {
  fullName?: string;
  firstName?: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
  projectName?: string;
  telefone?: string;
  phone?: string;
  phoneFormatted?: string;
}

const RegisterPageWithLex: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Estados principais
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(`lex_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
  const [currentStep, setCurrentStep] = useState<string>('name');
  const [registrationData, setRegistrationData] = useState<RegistrationData>({});
  const [progress, setProgress] = useState(10);
  
  // Estados para telha de senha
  const [showPasswordTile, setShowPasswordTile] = useState(false);
  const [showPasswordCompleted, setShowPasswordCompleted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);
  
  // Controles de fluxo
  const [messageCount, setMessageCount] = useState(0);
  
  // Ref para scroll automático
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    initializeLexSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('📜 Scroll effect - showPasswordTile:', showPasswordTile, 'messages:', messages.length);
    scrollToBottom();
  }, [messages, showPasswordTile]);

  // Monitorar mudanças no estado showPasswordCompleted  
  useEffect(() => {
    console.log('🔒 Estado showPasswordCompleted mudou para:', showPasswordCompleted, 'currentStep:', currentStep);
  }, [showPasswordCompleted, currentStep]);

  // Esconder componente bloqueado apenas quando sair do step de company (MAS NÃO no edit_data)
  useEffect(() => {
    if (currentStep !== 'password' && currentStep !== 'company' && currentStep !== 'edit_data' && showPasswordCompleted) {
      console.log('🔒 Escondendo componente bloqueado - currentStep:', currentStep);
      setShowPasswordCompleted(false);
    }
  }, [currentStep, showPasswordCompleted]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const initializeLexSession = async () => {
    console.log('🚀 Inicializando sessão LEX...');
    setIsLoading(true);
    
    try {
      // Nova sessão - usar APENAS o backend
      const response = await lexService.sendMessage('iniciar', {
        type: 'registration',
        pageType: 'registration',
        currentPage: 'register'
      });

      console.log('📦 Resposta de inicialização:', response);

      // Verificar se temos resposta válida
      if (!response || !response.message) {
        console.error('❌ Resposta inválida do backend:', response);
        setIsLoading(false);
        return;
      }

      // Usar EXATAMENTE o que vem do backend
      const lexMessage: Message = {
        id: `lex_${Date.now()}`,
        text: response.message,
        sender: 'lex',
        timestamp: new Date(),
        suggestions: response.suggestions || [],
        avatar: '🤖'
      };

      setMessages([lexMessage]);
      if (response.step) setCurrentStep(response.step);
      if (response.progress) setProgress(response.progress);
      
      // Focar no campo de input após a resposta inicial do LEX
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      
      // Atualizar dados se disponíveis
      if (response.data) {
        setRegistrationData(response.data);
      }
      
      // Mostrar telha de senha APENAS se backend disser
      if (response.step === 'password') {
        setShowPasswordTile(true);
      }

      console.log('✅ LEX inicializado com sucesso - Mensagem:', response.message.substring(0, 50));

    } catch (error) {
      console.error('❌ Erro ao inicializar LEX:', error);
    }
    
    setIsLoading(false);
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user_${Date.now()}`,
      text: currentMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsLoading(true);
    setMessageCount(prev => prev + 1);

    // Simular tempo de "digitação" do LEX para realismo
    const typingDelay = Math.min(Math.max(currentMessage.length * 120, 2500), 6000);

    try {
      console.log('🚀 Enviando mensagem para LEX:', {
        sessionId,
        message: currentMessage,
        currentStep,
        registrationData
      });

      // Fazer a requisição em paralelo com o delay de digitação
      const [response] = await Promise.all([
        lexService.sendMessage(currentMessage, {
          type: 'registration',
          pageType: 'registration',
          currentPage: 'register',
          currentStep,
          registrationData
        }),
        new Promise(resolve => setTimeout(resolve, typingDelay))
      ]);

      console.log('📦 Resposta recebida do LEX:', response);

      // Usar EXATAMENTE o que vem do backend - SEM fallbacks
      const backendData = response.data || response.context?.conversationState?.registrationData || {};
      
      // IMPORTANTE: Preservar dados já coletados localmente (como senha)
      setRegistrationData(prev => ({
        ...prev,  // Manter dados locais (senha, etc.)
        ...backendData  // Atualizar com dados do backend
      }));
      
      console.log('📋 Dados coletados do LEX:', backendData);
      console.log('📋 Dados completos (local + backend):', { ...registrationData, ...backendData });
      
      // Extrair step de qualquer lugar que o backend retorne
      const backendStep = response.step || 
                         response.context?.currentStep || 
                         response.context?.conversationState?.step;
                         
      if (backendStep) setCurrentStep(backendStep);
      if (response.progress) setProgress(response.progress);

      // Telha de senha: verificar todas as possíveis fontes
      const isPasswordStep = backendStep === 'password';
      console.log('🔍 Debug step detection:', {
        responseStep: response.step,
        currentStep: response.context?.currentStep,
        conversationStep: response.context?.conversationState?.step,
        finalStep: backendStep,
        isPasswordStep
      });
      
      // MUDANÇA: Só ATIVAR a telha se estivermos no step password
      // Mas NUNCA desativar após ter sido ativada (manter segurança visual)
      if (isPasswordStep) {
        setShowPasswordTile(true);
      }
      // NÃO fazer setShowPasswordTile(false) - manter telha visível uma vez ativada

      // Verificar se precisa mostrar botões para email existente - confiar apenas no backend
      const showEmailActions = response.metadata?.showLoginButton === true;
      
      console.log('🔍 Debug Email Exists:', {
        metadata: response.metadata,
        showLoginButton: response.metadata?.showLoginButton,
        showEmailActions,
        actions: response.actions,
        errorType: response.metadata?.errorType
      });

      const lexMessage: Message = {
        id: `lex_${Date.now()}`,
        text: response.message,
        sender: 'lex',
        timestamp: new Date(),
        suggestions: response.suggestions,
        avatar: '🤖',
        showEmailExistsActions: showEmailActions,
        emailForLogin: showEmailActions ? (response.actions?.find(a => a.type === 'redirect_to_login')?.data?.email || registrationData.email) : undefined
      };

      setMessages(prev => [...prev, lexMessage]);

      // Processar actions se existirem
      if (response.actions && response.actions.length > 0) {
        for (const action of response.actions) {
          switch (action.type) {
            case 'request_password':
            case 'request_password_confirm':
              console.log('🔒 Action request_password detectada - ativando telha');
              setShowPasswordTile(true);
              setCurrentStep('password');
              break;
              
            case 'registration_completed_blocked':
              // ✅ NOVO: Usar mesma navegação do registro antigo
              if (action.data.nextStep === 'email_verification') {
                navigate('/auth/verify-email', { 
                  replace: true,
                  state: { 
                    email: action.data.email,
                    userId: action.data.userId,
                    message: action.data.message || "Um email de verificação foi enviado para sua caixa de entrada.",
                    fromLex: true
                  } 
                });
              } else {
                // Fallback para rota antiga
                navigate('/auth/email-verification', { 
                  state: { 
                    email: action.data.email,
                    userId: action.data.userId,
                    fromLex: true
                  } 
                });
              }
              return;
            case 'redirect_to_traditional_register':
              // Redirecionar para registro tradicional com dados preenchidos
              navigate('/register-full', { 
                state: { 
                  prefilledData: action.data 
                } 
              });
              return;
          }
        }
      }

      // Focar no campo de input após a resposta do LEX (pequeno delay para suavidade)
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setIsLoading(false);
    }
  };


  const handlePasswordSubmit = async () => {
    // Validações já são feitas pelo PasswordStrengthIndicator e estado do botão
    // NÃO esconder a telha - manter como "protegida/confirmada"
    // setShowPasswordTile(false); // ❌ Removido - manter telha visível
    console.log('🔒 Ativando showPasswordCompleted = true');
    setShowPasswordCompleted(true);

    // NÃO mostrar senha no chat - será processada diretamente
    setIsLoading(true);

    try {
      // Enviar sinal especial de senha definida com os dados da senha no contexto
      const currentRegistrationData = {
        ...registrationData,
        password: registrationData.password,
        passwordConfirm: registrationData.passwordConfirm
      };
      
      console.log('🔒 Enviando dados da senha:', currentRegistrationData);
      
      const response = await lexService.sendMessage('***SENHA_DEFINIDA***', {
        type: 'registration',
        pageType: 'registration',
        currentPage: 'register',
        currentStep: 'password',
        registrationData: currentRegistrationData
      });

      if (response.data) {
        setRegistrationData(prev => ({ ...prev, ...response.data }));
      }

      if (response.step) {
        setCurrentStep(response.step);
      }

      if (response.progress) {
        setProgress(response.progress);
      }

      const lexMessage: Message = {
        id: `lex_${Date.now()}`,
        text: response.message,
        sender: 'lex',
        timestamp: new Date(),
        suggestions: response.suggestions,
        avatar: '🤖'
      };

      setMessages(prev => [...prev, lexMessage]);

      // Focar no campo de input após a resposta do LEX
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      if (response.step === 'completed') {
        setTimeout(() => {
          window.location.href = '/auth/login';
        }, 2000);
      }

    } catch (error) {
      console.error('Erro ao enviar senha:', error);
      // Erro será mostrado pelo LEX na conversa
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (field: 'password' | 'passwordConfirm', value: string) => {
    setRegistrationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Callback para receber resultado da validação de senha
  const handlePasswordValidation = (result: PasswordValidationResult) => {
    setPasswordValidation(result);
  };

  const handlePasswordCancel = async () => {
    // Mostrar aviso antes de limpar
    const confirmRestart = window.confirm(
      '⚠️ Cancelar irá apagar todos os dados inseridos e recomeçar o registro.\n\nTem certeza que deseja continuar?'
    );
    
    if (!confirmRestart) {
      return; // Usuário cancelou a ação
    }

    try {
      // Excluir sessão LEX completamente
      await lexService.deleteRegistrationSession(sessionId);
      
      // Limpar todos os dados
      setRegistrationData({
        fullName: '',
        email: '',
        password: '',
        passwordConfirm: ''
      });
      
      // Fechar telha de senha e componente bloqueado
      setShowPasswordTile(false);
      setShowPasswordCompleted(false);
      
      // Resetar estado para início
      setCurrentStep('name');
      setProgress(10);
      setMessages([]);
      
      // Gerar nova sessão
      const newSessionId = `lex_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setSessionId(newSessionId);
      
      // Mostrar mensagem de reinício
      const restartMessage: Message = {
        id: `lex_${Date.now()}`,
        text: '🔄 Processo reiniciado! Olá! Sou o LEX, seu assistente para criar sua conta na Simplex. \n\nVou te ajudar de forma simples e segura. Para começar, qual é o seu nome completo?',
        sender: 'lex',
        timestamp: new Date(),
        avatar: '🤖'
      };

      setMessages([restartMessage]);

    } catch (error) {
      console.error('Erro ao cancelar e reiniciar:', error);
      
      // Fallback: apenas limpar dados localmente
      setRegistrationData({
        fullName: '',
        email: '',
        password: '',
        passwordConfirm: ''
      });
      setShowPasswordTile(false);
      setCurrentStep('name');
      setProgress(10);
      setMessages([]);
      
      const fallbackMessage: Message = {
        id: `lex_${Date.now()}`,
        text: '🔄 Processo reiniciado! Como posso ajudá-lo a criar sua conta?',
        sender: 'lex',
        timestamp: new Date(),
        avatar: '🤖'
      };

      setMessages([fallbackMessage]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderMessage = (message: Message) => (
    <Fade key={message.id} in={true} timeout={500}>
      <Box 
        sx={{ 
          mb: 2, 
          display: 'flex', 
          justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
          gap: 1
        }}
      >
        {message.sender !== 'user' && (
          <Box
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              order: 0,
              fontSize: '1.5rem'
            }}
          >
            {message.sender === 'lex' ? '🤖' : '⚠️'}
          </Box>
        )}
        
        <Box
          sx={{
            maxWidth: '75%',
            order: message.sender === 'user' ? 0 : 1
          }}
        >
          <Paper
            sx={{
              p: 2,
              bgcolor: message.sender === 'user' 
                ? VERDE_MENTA 
                : message.sender === 'system'
                  ? withAlpha('#FF9800', 0.1)
                  : withAlpha(VERDE_MENTA, 0.05),
              color: message.sender === 'user' 
                ? 'white' 
                : message.sender === 'system'
                  ? '#F57C00'
                  : 'text.primary',
              borderRadius: message.sender === 'user' 
                ? '18px 18px 4px 18px' 
                : '18px 18px 18px 4px',
              border: message.sender === 'user' 
                ? 'none' 
                : `1px solid ${withAlpha(VERDE_MENTA, 0.1)}`,
              position: 'relative'
            }}
          >
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{ __html: message.text }}
              sx={{ 
                mb: 0,
                '& strong': { 
                  fontWeight: 600,
                  color: message.sender === 'user' ? 'white' : VERDE_MENTA
                },
                '& p': {
                  margin: '0 0 4px 0',
                  '&:last-child': { marginBottom: 0 }
                },
                whiteSpace: 'pre-line',
                lineHeight: 1.2
              }}
            />
            
            {/* Link para email existente */}
            {message.showEmailExistsActions && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  component="a"
                  onClick={async () => {
                    try {
                      // Excluir sessão do backend
                      const response = await fetch(buildApiUrl(`/api/lex/session/${sessionId}`), {
                        method: 'DELETE',
                        headers: {
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      if (!response.ok) {
                        console.error('Erro ao excluir sessão LEX do backend:', response.status);
                      }
                    } catch (error) {
                      console.error('Erro ao excluir sessão LEX do backend:', error);
                    }
                    
                    // Limpar sessão do frontend (localStorage)
                    lexService.clearSession();
                    
                    // Limpar dados locais do registro
                    setRegistrationData({
                      fullName: '',
                      email: '',
                      password: '',
                      passwordConfirm: ''
                    });
                    setMessages([]);
                    setCurrentStep('name');
                    setProgress(0);
                    
                    // Navegar para login com email pré-preenchido
                    navigate('/login', { 
                      state: { email: message.emailForLogin } 
                    });
                  }}
                  sx={{
                    color: VERDE_MENTA,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    borderBottom: `2px solid ${VERDE_MENTA}`,
                    cursor: 'pointer',
                    display: 'inline-block',
                    pb: 0.3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: withAlpha(VERDE_MENTA, 0.8),
                      borderBottomColor: withAlpha(VERDE_MENTA, 0.8),
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  🔗 Ir para a página de login
                </Typography>
              </Box>
            )}
            
            {/* Final Action Buttons for edit_data step */}
            {currentStep === 'edit_data' && message.sender === 'lex' && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={async () => {
                    setIsLoading(true);
                    
                    try {
                      console.log('🚀 Registro via LEX: Usando MESMO endpoint da página tradicional');
                      
                      // ✅ USAR EXATAMENTE O MESMO FLUXO DA PÁGINA DE REGISTRO TRADICIONAL
                      const { authService } = await import('../../services/authService');
                      
                      // Preparar dados no formato EXATO do registro tradicional
                      const registerData = {
                        fullName: registrationData.fullName || '',
                        email: registrationData.email || '',
                        password: registrationData.password || '',
                        projectName: registrationData.projectName || '',
                        phone: registrationData.telefone || registrationData.phone || '',
                        website: '',
                        plan: 'basic'
                      };
                      
                      console.log('📦 Dados para registro (MESMO formato do tradicional):', registerData);
                      
                      // ✅ CHAMAR O MESMO MÉTODO QUE A PÁGINA TRADICIONAL USA
                      const result = await authService.register(registerData);
                      
                      console.log('📋 Resultado do registro:', result);
                      
                      if (result.success) {
                        // 🧹 LIMPAR SESSÃO NO SERVIDOR APÓS SUCESSO
                        try {
                          await lexService.deleteRegistrationSession(sessionId);
                          console.log('✅ Sessão LEX limpa no servidor');
                        } catch (error) {
                          console.error('⚠️ Erro ao limpar sessão LEX:', error);
                          // Não bloquear o fluxo se falhar a limpeza
                        }
                        
                        // Limpar sessão local também
                        lexService.clearSession();
                        
                        // ✅ SEGUIR O MESMO FLUXO DA PÁGINA TRADICIONAL
                        if (result.data?.nextStep === 'email_verification') {
                          // Redirecionar para verificação de email
                          navigate('/auth/verify-email', { 
                            replace: true,
                            state: { 
                              email: result.data.email,
                              userId: result.data.userId,
                              message: result.data.message || "Um email de verificação foi enviado para sua caixa de entrada.",
                              fromLex: true
                            } 
                          });
                        } else {
                          // Fallback: ir para dashboard se não precisar verificar email
                          navigate('/owner/dashboard', { replace: true });
                        }
                      } else {
                        // ❌ ERRO: Mostrar mensagem
                        console.error('❌ Erro no registro:', result.message);
                        alert(`Erro ao criar conta: ${result.message}`);
                      }
                      
                    } catch (error) {
                      console.error('❌ Erro durante registro:', error);
                      alert('Erro inesperado ao criar conta. Tente novamente.');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: VERDE_MENTA,
                    color: 'white',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: withAlpha(VERDE_MENTA, 0.8),
                    }
                  }}
                >
                  ✅ Confirmar dados e criar conta
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={async () => {
                    console.log('🔍 Dados para preencher:', registrationData);
                    
                    // 🧹 LIMPAR SESSÃO NO SERVIDOR (mas preservar dados locais)
                    try {
                      await lexService.deleteRegistrationSession(sessionId);
                      console.log('✅ Sessão LEX limpa no servidor antes de editar');
                    } catch (error) {
                      console.error('⚠️ Erro ao limpar sessão LEX:', error);
                      // Não bloquear o fluxo se falhar a limpeza
                    }
                    
                    // Limpar sessão local também
                    lexService.clearSession();
                    
                    // Navegar com dados preservados
                    navigate('/register-full', { 
                      state: { 
                        prefilledData: registrationData 
                      } 
                    });
                  }}
                  sx={{
                    borderColor: VERDE_MENTA,
                    color: VERDE_MENTA,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    py: 1.5,
                    '&:hover': {
                      borderColor: VERDE_MENTA,
                      backgroundColor: withAlpha(VERDE_MENTA, 0.1),
                    }
                  }}
                >
                  ✏️ Editar dados
                </Button>
              </Box>
            )}
            
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mt: 0.5,
                opacity: 0.7,
                fontSize: '0.75rem',
                textAlign: message.sender === 'user' ? 'right' : 'left'
              }}
            >
              {message.timestamp.toLocaleTimeString('pt-BR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </Typography>
          </Paper>
        </Box>

        {message.sender === 'user' && (
          <Avatar
            sx={{
              bgcolor: theme.palette.secondary.main,
              width: 36,
              height: 36,
              order: 1
            }}
          >
            <PersonIcon />
          </Avatar>
        )}
      </Box>
    </Fade>
  );

  const renderPasswordCompleted = () => {
    console.log('🔒 renderPasswordCompleted - showPasswordCompleted:', showPasswordCompleted, 'currentStep:', currentStep);
    if (!showPasswordCompleted) return null;

    return (
      <Box
        sx={{
          mb: 1.5,
          p: 1.5,
          bgcolor: withAlpha('#9E9E9E', 0.03),
          border: `1px solid ${withAlpha('#9E9E9E', 0.2)}`,
          borderRadius: 1.5,
          boxShadow: `0 2px 8px ${withAlpha('#9E9E9E', 0.08)}`,
          position: 'relative',
          opacity: 0.8
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ color: '#9E9E9E', mr: 1, fontSize: '1.2rem' }} />
            <Typography variant="body2" sx={{ color: '#9E9E9E', fontWeight: 600, fontSize: '0.9rem' }}>
              Campo Seguro Utilizado
            </Typography>
          </Box>
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: '#4CAF50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            ✓
          </Box>
        </Box>

        <Typography variant="caption" sx={{ 
          color: withAlpha(TEXTO_SECUNDARIO, 0.7), 
          fontSize: '0.75rem',
          lineHeight: 1.3,
          display: 'block',
          mb: 1
        }}>
          Área protegida usada para definir sua senha com segurança.
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5,
          p: 0.75,
          bgcolor: withAlpha('#4CAF50', 0.05),
          borderRadius: 0.75,
          border: `1px solid ${withAlpha('#4CAF50', 0.15)}`
        }}>
          <SecurityIcon sx={{ color: '#4CAF50', fontSize: '0.9rem' }} />
          <Typography variant="caption" sx={{ 
            color: '#4CAF50', 
            fontSize: '0.7rem',
            fontWeight: 600
          }}>
            Senha criptografada com sucesso
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderPasswordTile = () => {
    console.log('🔐 renderPasswordTile chamado - showPasswordTile:', showPasswordTile);
    if (!showPasswordTile) return null;

    // Se senha foi completada, mostrar estado "protegido"
    if (showPasswordCompleted) {
      console.log('🔒 Renderizando telha no estado PROTEGIDO');
      return (
        <Box
          sx={{
            mb: 2,
            p: 2.5,
            bgcolor: withAlpha(VERDE_MENTA, 0.08),
            border: `2px solid ${withAlpha(VERDE_MENTA, 0.6)}`,
            borderRadius: 2,
            boxShadow: `0 4px 16px ${withAlpha(VERDE_MENTA, 0.25)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SecurityIcon sx={{ color: VERDE_MENTA, mr: 1.5, fontSize: '1.6rem' }} />
              <Typography variant="h6" sx={{ color: VERDE_MENTA, fontWeight: 600 }}>
                ✅ Senha Definida com Segurança
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: withAlpha(TEXTO_SECUNDARIO, 0.8), fontStyle: 'italic' }}>
            🔐 Sua senha foi criptografada e está protegida. Continue o cadastro!
          </Typography>
        </Box>
      );
    }

    console.log('🎯 Renderizando telha de senha completa');
    return (
      <Box
        sx={{
          mb: 2,
          p: 2.5,
          bgcolor: withAlpha(VERDE_MENTA, 0.05),
          border: `2px solid ${withAlpha(VERDE_MENTA, 0.4)}`,
          borderRadius: 2,
          boxShadow: `0 4px 16px ${withAlpha(VERDE_MENTA, 0.15)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ color: VERDE_MENTA, mr: 1.5, fontSize: '1.6rem' }} />
            <Typography variant="h6" sx={{ color: VERDE_MENTA, fontWeight: 600 }}>
              Criação de Senha Segura
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: withAlpha(TEXTO_SECUNDARIO, 1), fontSize: '0.8rem', fontStyle: 'italic' }}>
            🔐 Senha criptografada para segurança
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          {/* Layout responsivo: campos empilhados em mobile, lado a lado no desktop */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 1.5 }, 
            mb: 2, 
            alignItems: 'flex-start' 
          }}>
            <TextField
              type={showPassword ? 'text' : 'password'}
              label="Nova Senha"
              placeholder="Digite sua senha"
              value={registrationData.password || ''}
              onChange={(e) => handlePasswordChange('password', e.target.value)}
              variant="outlined"
              size={window.innerWidth < 768 ? "medium" : "small"}
              fullWidth
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: withAlpha(FUNDO_PAPEL, 0.7),
                  borderRadius: 1.5,
                  fontSize: { xs: '1rem', md: '0.9rem' },
                  minHeight: { xs: '56px', md: 'auto' },
                  transition: 'all 0.2s ease',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: withAlpha(VERDE_MENTA, 0.4),
                  },
                  '&.Mui-focused': {
                    backgroundColor: withAlpha(FUNDO_PAPEL, 0.9),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: VERDE_MENTA,
                      borderWidth: 2,
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  color: withAlpha(TEXTO_SECUNDARIO, 0.7),
                  fontSize: { xs: '1rem', md: '0.85rem' },
                  '&.Mui-focused': {
                    color: VERDE_MENTA,
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: withAlpha(VERDE_MENTA, 0.7) }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirmar Senha"
              placeholder="Confirme a senha"
              value={registrationData.passwordConfirm || ''}
              onChange={(e) => handlePasswordChange('passwordConfirm', e.target.value)}
              variant="outlined"
              size={window.innerWidth < 768 ? "medium" : "small"}
              fullWidth
              disabled={!registrationData.password}
              error={!!(registrationData.passwordConfirm && registrationData.password !== registrationData.passwordConfirm)}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: withAlpha(FUNDO_PAPEL, 0.7),
                  borderRadius: 1.5,
                  fontSize: { xs: '1rem', md: '0.9rem' },
                  minHeight: { xs: '56px', md: 'auto' },
                  transition: 'all 0.2s ease',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: withAlpha(VERDE_MENTA, 0.4),
                  },
                  '&.Mui-focused': {
                    backgroundColor: withAlpha(FUNDO_PAPEL, 0.9),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: VERDE_MENTA,
                      borderWidth: 2,
                    }
                  },
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  color: withAlpha(TEXTO_SECUNDARIO, 0.7),
                  fontSize: { xs: '1rem', md: '0.85rem' },
                  '&.Mui-focused': {
                    color: VERDE_MENTA,
                  },
                  '&.Mui-error': {
                    color: '#f44336',
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: withAlpha(VERDE_MENTA, 0.7) }}
                    >
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText={
                registrationData.passwordConfirm && registrationData.password !== registrationData.passwordConfirm
                  ? "Não coincidem"
                  : registrationData.passwordConfirm && registrationData.password === registrationData.passwordConfirm
                  ? "✓ Coincidem"
                  : ""
              }
            />
          </Box>

          {/* Botões centralizados embaixo dos campos em mobile */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'row', md: 'column' },
            gap: { xs: 2, md: 0.8 }, 
            justifyContent: { xs: 'center', md: 'flex-start' },
            ml: { xs: 0, md: 0.5 },
            mt: { xs: 1, md: 0 }
          }}>
            <Button
              variant="outlined"
              size={window.innerWidth < 768 ? "medium" : "small"}
              onClick={handlePasswordCancel}
              sx={{
                borderColor: withAlpha(VERDE_MENTA, 0.3),
                color: withAlpha(VERDE_MENTA, 0.8),
                fontSize: { xs: '0.9rem', md: '0.75rem' },
                py: { xs: 1, md: 0.6 },
                px: { xs: 3, md: 1.5 },
                minWidth: { xs: '120px', md: '80px' },
                height: { xs: '44px', md: '32px' },
                '&:hover': {
                  borderColor: withAlpha(VERDE_MENTA, 0.5),
                  backgroundColor: withAlpha(VERDE_MENTA, 0.03),
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              size={window.innerWidth < 768 ? "medium" : "small"}
              onClick={handlePasswordSubmit}
              disabled={
                !registrationData.password || 
                !registrationData.passwordConfirm || 
                registrationData.password !== registrationData.passwordConfirm ||
                (passwordValidation !== null && !passwordValidation.isValid)
              }
              sx={{
                background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, ${withAlpha(VERDE_MENTA, 0.85)} 100%)`,
                color: 'white',
                fontSize: { xs: '0.9rem', md: '0.75rem' },
                py: { xs: 1, md: 0.6 },
                px: { xs: 3, md: 1.5 },
                minWidth: { xs: '120px', md: '80px' },
                height: { xs: '44px', md: '32px' },
                boxShadow: `0 2px 8px ${withAlpha(VERDE_MENTA, 0.25)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${withAlpha(VERDE_MENTA, 0.9)} 0%, ${withAlpha(VERDE_MENTA, 0.75)} 100%)`,
                  boxShadow: `0 3px 12px ${withAlpha(VERDE_MENTA, 0.35)}`,
                },
                '&.Mui-disabled': {
                  background: withAlpha('#ccc', 0.3),
                  boxShadow: 'none',
                }
              }}
            >
              Confirmar
            </Button>
          </Box>

          {/* Indicador de força da senha */}
          <Box sx={{ mt: 1 }}>
            <PasswordStrengthIndicator
              password={registrationData.password || ''}
              userContext={{
                name: registrationData.fullName,
                email: registrationData.email,
                firstName: registrationData.firstName
              }}
              showDetails={true}
              showRules={false}
              onValidation={handlePasswordValidation}
              debounceMs={300}
            />
          </Box>
        </Box>

      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: FUNDO_ESCURO,
        backgroundImage: `radial-gradient(circle at 20% 50%, ${withAlpha(VERDE_MENTA, 0.1)} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${withAlpha(VERDE_MENTA, 0.05)} 0%, transparent 50%)`,
        overflow: "auto",
        py: 4
      }}
    >
      {/* Botão Home */}
      <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
        <Tooltip title="Voltar para a página inicial">
          <IconButton
            onClick={() => { window.location.href = '/'; }}
            size="medium"
            sx={{ 
              color: VERDE_MENTA,
              background: withAlpha(VERDE_MENTA, 0.1),
              '&:hover': {
                background: withAlpha(VERDE_MENTA, 0.2),
              }
            }}
          >
            <HomeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Seletor de idioma */}
      <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
        <Tooltip title="Selecionar idioma">
          <IconButton
            size="medium"
            sx={{ 
              color: VERDE_MENTA,
              background: withAlpha(VERDE_MENTA, 0.1),
              '&:hover': {
                background: withAlpha(VERDE_MENTA, 0.2),
              }
            }}
          >
            <LanguageIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Container principal */}
      <Container 
        maxWidth="md" 
        sx={{
          backgroundColor: FUNDO_PAPEL,
          borderRadius: 4,
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
          overflow: "hidden",
          height: "85vh",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: `1px solid ${withAlpha(VERDE_MENTA, 0.1)}`,
            background: `linear-gradient(135deg, ${withAlpha(VERDE_MENTA, 0.05)} 0%, ${withAlpha(VERDE_MENTA, 0.02)} 100%)`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                sx={{ 
                  bgcolor: withAlpha(VERDE_MENTA, 0.15),
                  color: VERDE_MENTA,
                  width: 48,
                  height: 48
                }}
              >
                <PsychologyIcon sx={{ fontSize: '1.8rem' }} />
              </Avatar>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: VERDE_MENTA, 
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  LEX - Assistente IA
                  <AutoAwesomeIcon sx={{ fontSize: '1.2rem' }} />
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: TEXTO_SECUNDARIO,
                    fontStyle: 'italic'
                  }}
                >
                  Criação de conta inteligente e personalizada
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={`${Math.round(progress)}% concluído`}
                size="small"
                sx={{
                  bgcolor: withAlpha(VERDE_MENTA, 0.1),
                  color: VERDE_MENTA,
                  fontWeight: 600
                }}
              />
              {messageCount >= 20 && (
                <Chip 
                  label={`${messageCount}/30 mensagens`}
                  size="small"
                  sx={{
                    bgcolor: withAlpha('#F44336', 0.1),
                    color: '#F44336',
                    fontWeight: 600
                  }}
                />
              )}
            </Box>
          </Box>
          
          {/* Barra de progresso */}
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{
              mt: 2,
              height: 6,
              borderRadius: 3,
              bgcolor: withAlpha(VERDE_MENTA, 0.1),
              '& .MuiLinearProgress-bar': {
                backgroundColor: VERDE_MENTA,
                borderRadius: 3,
              }
            }}
          />
        </Box>

        {/* Área de mensagens */}
        <Box 
          sx={{ 
            flex: 1, 
            p: 3, 
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {/* Mensagens do chat (exceto a última se componente bloqueado estiver ativo) */}
          {messages.slice(0, showPasswordCompleted ? -1 : messages.length).map(renderMessage)}
          
          {/* Telha de senha posicionada após as mensagens */}
          {renderPasswordTile()}
          
          {/* Componente bloqueado após confirmação da senha */}
          {renderPasswordCompleted()}
          
          {/* Última mensagem quando componente bloqueado está ativo */}
          {showPasswordCompleted && messages.length > 0 && renderMessage(messages[messages.length - 1])}
          
          {/* Indicador de digitação */}
          {isLoading && (
            <Fade in={true}>
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 1
              }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}
                >
                  🤖
                </Box>
                
                <Paper
                  sx={{
                    p: 2,
                    maxWidth: '75%',
                    bgcolor: withAlpha(VERDE_MENTA, 0.05),
                    borderRadius: '18px 18px 18px 4px',
                    border: `1px solid ${withAlpha(VERDE_MENTA, 0.1)}`,
                    position: 'relative'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: VERDE_MENTA,
                            animation: 'typingBounce 1.8s ease-in-out infinite',
                            animationDelay: `${i * 0.3}s`,
                            '@keyframes typingBounce': {
                              '0%, 60%, 100%': {
                                opacity: 0.3,
                                transform: 'translateY(0) scale(0.8)'
                              },
                              '30%': {
                                opacity: 1,
                                transform: 'translateY(-8px) scale(1.1)'
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: withAlpha(TEXTO_SECUNDARIO, 0.8),
                        fontStyle: 'italic',
                        fontSize: '0.85rem'
                      }}
                    >
                      LEX está digitando...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Fade>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ 
          p: 3, 
          borderTop: `2px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
          background: `linear-gradient(135deg, ${withAlpha(VERDE_MENTA, 0.03)} 0%, ${withAlpha(VERDE_MENTA, 0.01)} 100%)`,
          boxShadow: `0 -4px 20px ${withAlpha(VERDE_MENTA, 0.1)}`
        }}>
          {messageCount > 5 && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/register')}
                sx={{
                  borderColor: withAlpha(TEXTO_SECUNDARIO, 0.3),
                  color: TEXTO_SECUNDARIO,
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 2,
                  '&:hover': {
                    borderColor: VERDE_MENTA,
                    color: VERDE_MENTA,
                    backgroundColor: withAlpha(VERDE_MENTA, 0.05)
                  }
                }}
              >
                Preferir formulário tradicional
              </Button>
            </Box>
          )}
          

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              ref={inputRef}
              multiline
              maxRows={3}
              placeholder={
                (showPasswordTile && !showPasswordCompleted) ? "👆 Use o campo seguro acima para definir sua senha..." :
                currentStep === 'edit_data' ? "🔒 Use os botões acima para finalizar ou editar" :
                "Responda ao LEX aqui..."
              }
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading || (showPasswordTile && !showPasswordCompleted) || currentStep === 'edit_data'}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: withAlpha(VERDE_MENTA, 0.03),
                  borderRadius: 2,
                  boxShadow: `0 2px 8px ${withAlpha('#000', 0.08)}`,
                  border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: withAlpha(VERDE_MENTA, 0.4),
                    backgroundColor: withAlpha(VERDE_MENTA, 0.05),
                  },
                  '&.Mui-focused': {
                    borderColor: VERDE_MENTA,
                    backgroundColor: withAlpha(VERDE_MENTA, 0.08),
                    boxShadow: `0 0 0 3px ${withAlpha(VERDE_MENTA, 0.2)}, 0 0 20px ${withAlpha(VERDE_MENTA, 0.15)}`,
                    transform: 'scale(1.01)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: withAlpha('#f5f5f5', 0.5),
                    borderColor: withAlpha('#ccc', 0.3),
                  }
                },
                '& .MuiInputBase-input': {
                  py: 0,
                  px: 2,
                  fontSize: '1rem',
                  lineHeight: 0.9,
                  color: 'text.primary',
                  minHeight: '0.6rem',
                  height: '1rem'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: withAlpha(TEXTO_SECUNDARIO, 0.7),
                  opacity: 1,
                  fontStyle: 'italic',
                  fontSize: '0.95rem'
                }
              }}
            />
            <Button
              variant="contained"
              onClick={sendMessage}
              disabled={!currentMessage.trim() || isLoading || (showPasswordTile && !showPasswordCompleted) || currentStep === 'edit_data'}
              sx={{ 
                minWidth: 80,
                height: 48,
                borderRadius: 2,
                px: 2,
                background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, ${withAlpha(VERDE_MENTA, 0.85)} 100%)`,
                boxShadow: `0 2px 8px ${withAlpha(VERDE_MENTA, 0.25)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: `linear-gradient(135deg, ${withAlpha(VERDE_MENTA, 0.9)} 0%, ${withAlpha(VERDE_MENTA, 0.75)} 100%)`,
                  boxShadow: `0 3px 12px ${withAlpha(VERDE_MENTA, 0.35)}`,
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                '&.Mui-disabled': {
                  background: withAlpha('#ccc', 0.4),
                  boxShadow: 'none'
                }
              }}
            >
              <SendIcon sx={{ fontSize: '1.2rem', mr: 0.5 }} />
              Enviar
            </Button>
          </Box>
          
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              mt: 1,
              color: TEXTO_SECUNDARIO,
              textAlign: 'center'
            }}
          >
            Pressione Enter para enviar • Shift+Enter para nova linha
          </Typography>
          
          {/* Link para login em mobile */}
          <Box 
            sx={{ 
              display: { xs: 'flex', sm: 'none' }, // Só mostrar em mobile
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${withAlpha(VERDE_MENTA, 0.1)}`
            }}
          >
            <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
              Já tem conta?
            </Typography>
            <Button
              onClick={() => navigate('/login')}
              sx={{
                color: VERDE_MENTA,
                fontWeight: 500,
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                fontSize: '0.875rem',
                "&:hover": {
                  backgroundColor: 'transparent',
                  textDecoration: "underline",
                },
              }}
            >
              Fazer login
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Link para login tradicional - movido para dentro do container */}
      <Box 
        sx={{ 
          position: 'absolute',
          bottom: { xs: 10, sm: 20 },
          left: '50%',
          transform: 'translateX(-50%)',
          display: { xs: 'none', sm: 'flex' }, // Esconder em mobile
          alignItems: 'center',
          gap: 1,
          zIndex: 1
        }}
      >
        <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
          Já tem conta?
        </Typography>
        <Button
          onClick={() => navigate('/login')}
          sx={{
            color: VERDE_MENTA,
            fontWeight: 500,
            textTransform: 'none',
            p: 0,
            minWidth: 'auto',
            "&:hover": {
              backgroundColor: 'transparent',
              textDecoration: "underline",
            },
          }}
        >
          Fazer login tradicional
        </Button>
      </Box>
    </Box>
  );
};

export default RegisterPageWithLex;